import { CollectionData, PaginationResults } from "@/lib/type";
import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithLogout } from "@/lib/baseQuery";
import {
  DailyDishIngredient,
  MenuItemIngredient,
  OptionValueIngredient,
  RecipeIngredientLink,
} from "./inventory.type";

export interface RecipeLinkPayload {
  ingredient: string;
  quantity: string;
}

export interface RecipeUpdatePayload {
  quantity: string;
}

export const RecipeApi = createApi({
  reducerPath: "recipeApi",
  tagTypes: [
    "menuItemIngredient",
    "dailyDishIngredient",
    "optionValueIngredient",
    "formulaComponentItemIngredient",
  ],
  baseQuery: baseQueryWithLogout,
  endpoints: (build) => ({
    getMenuItemIngredients: build.query<
      PaginationResults<MenuItemIngredient>,
      { menuItemUuid: string }
    >({
      query: ({ menuItemUuid }) => ({
        url: `menu_items/${menuItemUuid}/ingredients`,
        method: "GET",
      }),
      providesTags: ["menuItemIngredient"],
      transformResponse: (data: CollectionData<MenuItemIngredient>) => ({
        results: data["hydra:member"],
        count: data["hydra:totalItems"],
      }),
    }),

    linkIngredientToMenuItem: build.mutation<
      MenuItemIngredient,
      { menuItemUuid: string; data: RecipeLinkPayload }
    >({
      query: ({ menuItemUuid, data }) => ({
        url: `menu_items/${menuItemUuid}/ingredients`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["menuItemIngredient"],
    }),

    removeMenuItemIngredient: build.mutation<
      void,
      { menuItemUuid: string; mappingUuid: string }
    >({
      query: ({ menuItemUuid, mappingUuid }) => ({
        url: `menu_items/${menuItemUuid}/ingredients/${mappingUuid}`,
        method: "DELETE",
      }),
      invalidatesTags: ["menuItemIngredient"],
    }),

    updateMenuItemIngredient: build.mutation<
      MenuItemIngredient,
      { menuItemUuid: string; mappingUuid: string; data: RecipeUpdatePayload }
    >({
      query: ({ menuItemUuid, mappingUuid, data }) => ({
        url: `menu_items/${menuItemUuid}/ingredients/${mappingUuid}`,
        method: "PATCH",
        headers: { "Content-Type": "application/merge-patch+json" },
        body: JSON.stringify(data),
      }),
      invalidatesTags: ["menuItemIngredient"],
    }),

    getDailyDishIngredients: build.query<
      PaginationResults<DailyDishIngredient>,
      { dailyDishUuid: string }
    >({
      query: ({ dailyDishUuid }) => ({
        url: `daily_dishes/${dailyDishUuid}/ingredients`,
        method: "GET",
      }),
      providesTags: ["dailyDishIngredient"],
      transformResponse: (data: CollectionData<DailyDishIngredient>) => ({
        results: data["hydra:member"],
        count: data["hydra:totalItems"],
      }),
    }),

    linkIngredientToDailyDish: build.mutation<
      DailyDishIngredient,
      { dailyDishUuid: string; data: RecipeLinkPayload }
    >({
      query: ({ dailyDishUuid, data }) => ({
        url: `daily_dishes/${dailyDishUuid}/ingredients`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["dailyDishIngredient"],
    }),

    removeDailyDishIngredient: build.mutation<
      void,
      { dailyDishUuid: string; mappingUuid: string }
    >({
      query: ({ dailyDishUuid, mappingUuid }) => ({
        url: `daily_dishes/${dailyDishUuid}/ingredients/${mappingUuid}`,
        method: "DELETE",
      }),
      invalidatesTags: ["dailyDishIngredient"],
    }),

    updateDailyDishIngredient: build.mutation<
      DailyDishIngredient,
      { dailyDishUuid: string; mappingUuid: string; data: RecipeUpdatePayload }
    >({
      query: ({ dailyDishUuid, mappingUuid, data }) => ({
        url: `daily_dishes/${dailyDishUuid}/ingredients/${mappingUuid}`,
        method: "PATCH",
        headers: { "Content-Type": "application/merge-patch+json" },
        body: JSON.stringify(data),
      }),
      invalidatesTags: ["dailyDishIngredient"],
    }),

    getOptionValueIngredients: build.query<
      PaginationResults<OptionValueIngredient>,
      { optionValueUuid: string }
    >({
      query: ({ optionValueUuid }) => ({
        url: `menu_item_option_values/${optionValueUuid}/ingredients`,
        method: "GET",
      }),
      providesTags: ["optionValueIngredient"],
      transformResponse: (data: CollectionData<OptionValueIngredient>) => ({
        results: data["hydra:member"],
        count: data["hydra:totalItems"],
      }),
    }),

    linkIngredientToOptionValue: build.mutation<
      OptionValueIngredient,
      { optionValueUuid: string; data: RecipeLinkPayload }
    >({
      query: ({ optionValueUuid, data }) => ({
        url: `menu_item_option_values/${optionValueUuid}/ingredients`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["optionValueIngredient"],
    }),

    removeOptionValueIngredient: build.mutation<
      void,
      { optionValueUuid: string; mappingUuid: string }
    >({
      query: ({ optionValueUuid, mappingUuid }) => ({
        url: `menu_item_option_values/${optionValueUuid}/ingredients/${mappingUuid}`,
        method: "DELETE",
      }),
      invalidatesTags: ["optionValueIngredient"],
    }),

    updateOptionValueIngredient: build.mutation<
      OptionValueIngredient,
      { optionValueUuid: string; mappingUuid: string; data: RecipeUpdatePayload }
    >({
      query: ({ optionValueUuid, mappingUuid, data }) => ({
        url: `menu_item_option_values/${optionValueUuid}/ingredients/${mappingUuid}`,
        method: "PATCH",
        headers: { "Content-Type": "application/merge-patch+json" },
        body: JSON.stringify(data),
      }),
      invalidatesTags: ["optionValueIngredient"],
    }),

    getFormulaComponentItemIngredients: build.query<
      PaginationResults<RecipeIngredientLink>,
      { componentItemUuid: string }
    >({
      query: ({ componentItemUuid }) => ({
        url: `menu_formula_component_items/${componentItemUuid}/ingredients`,
        method: "GET",
      }),
      providesTags: ["formulaComponentItemIngredient"],
      transformResponse: (data: CollectionData<RecipeIngredientLink>) => ({
        results: data["hydra:member"],
        count: data["hydra:totalItems"],
      }),
    }),

    linkIngredientToFormulaComponentItem: build.mutation<
      RecipeIngredientLink,
      { componentItemUuid: string; data: RecipeLinkPayload }
    >({
      query: ({ componentItemUuid, data }) => ({
        url: `menu_formula_component_items/${componentItemUuid}/ingredients`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["formulaComponentItemIngredient"],
    }),

    removeFormulaComponentItemIngredient: build.mutation<
      void,
      { componentItemUuid: string; mappingUuid: string }
    >({
      query: ({ componentItemUuid, mappingUuid }) => ({
        url: `menu_formula_component_items/${componentItemUuid}/ingredients/${mappingUuid}`,
        method: "DELETE",
      }),
      invalidatesTags: ["formulaComponentItemIngredient"],
    }),

    updateFormulaComponentItemIngredient: build.mutation<
      RecipeIngredientLink,
      { componentItemUuid: string; mappingUuid: string; data: RecipeUpdatePayload }
    >({
      query: ({ componentItemUuid, mappingUuid, data }) => ({
        url: `menu_formula_component_items/${componentItemUuid}/ingredients/${mappingUuid}`,
        method: "PATCH",
        headers: { "Content-Type": "application/merge-patch+json" },
        body: JSON.stringify(data),
      }),
      invalidatesTags: ["formulaComponentItemIngredient"],
    }),
  }),
});

export const {
  useGetMenuItemIngredientsQuery,
  useLinkIngredientToMenuItemMutation,
  useRemoveMenuItemIngredientMutation,
  useUpdateMenuItemIngredientMutation,
  useGetDailyDishIngredientsQuery,
  useLinkIngredientToDailyDishMutation,
  useRemoveDailyDishIngredientMutation,
  useUpdateDailyDishIngredientMutation,
  useGetOptionValueIngredientsQuery,
  useLinkIngredientToOptionValueMutation,
  useRemoveOptionValueIngredientMutation,
  useUpdateOptionValueIngredientMutation,
  useGetFormulaComponentItemIngredientsQuery,
  useLinkIngredientToFormulaComponentItemMutation,
  useRemoveFormulaComponentItemIngredientMutation,
  useUpdateFormulaComponentItemIngredientMutation,
} = RecipeApi;
